import React from 'react';
import { HashRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { BottomNav } from './components/layout/BottomNav';
import { Home } from './pages/public/Home';
import { BrowseAccommodation } from './pages/public/BrowseAccommodation';
import { Pricing } from './pages/public/Pricing';
import { AccommodationDetail } from './pages/public/AccommodationDetail';
import { HowItWorks } from './pages/public/HowItWorks';
import { Contact } from './pages/public/Contact';
import { SiteMap } from './pages/public/SiteMap';
import { LocationPage } from './pages/public/LocationPage';
import { Agents } from './pages/public/Agents';
import { TermsAndConditions, PrivacyPolicy, RefundPolicy, PopiCompliance, CookiePolicy } from './pages/public/LegalPages';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { Profile } from './pages/auth/Profile';
import { StudentDashboard } from './pages/student/Dashboard';
import { AgentDashboard } from './pages/agent/Dashboard';
import { LandlordDashboard } from './pages/landlord/Dashboard';
import { AdminDashboard } from './pages/admin/Dashboard';
import { AgentForms } from './pages/agent/Forms';
import { NotificationsPage } from './pages/auth/NotificationsPage';
import { UserRole } from './types';

// Layout wrapper for public pages (adds Nav + Footer + BottomNav)
const PublicLayout = () => (
  <div className="min-h-screen flex flex-col bg-white font-sans pb-16 lg:pb-0">
    <Navbar />
    <div className="flex-grow">
      <Outlet />
    </div>
    <Footer />
    <BottomNav />
  </div>
);

// Protected Route Wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: UserRole }> = ({ children, requiredRole }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#2f5c96] border-t-transparent mb-4"></div>
          <p className="text-[#2f5c96] text-lg font-medium">Restoring your session...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin God Mode: Admins can access ANY dashboard
  if (user?.role === UserRole.ADMIN) {
    return <>{children}</>;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Redirect based on role if they try to access wrong area
    switch (user?.role) {
      case UserRole.STUDENT: return <Navigate to="/student/dashboard" replace />;
      case UserRole.AGENT: return <Navigate to="/agent/dashboard" replace />;
      case UserRole.LANDLORD: return <Navigate to="/landlord/dashboard" replace />;
      default: return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/browse" element={<BrowseAccommodation />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/sitemap" element={<SiteMap />} />
              <Route path="/agents" element={<Agents />} />

              {/* Legal Pages */}
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/popi-compliance" element={<PopiCompliance />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />

              {/* Location/GEO Pages (SEO-optimized) */}
              <Route path="/rooms/:city" element={<LocationPage />} />
              <Route path="/rooms/:city/:suburb" element={<LocationPage />} />

              {/* Property Detail Pages */}
              <Route path="/room/:slug" element={<AccommodationDetail />} />
              <Route path="/property/:id" element={<AccommodationDetail />} /> {/* Legacy support */}

              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected Profile Route - wrapped in PublicLayout for Nav/Footer but guarded */}
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />

              <Route path="/notifications" element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              } />
            </Route>

            {/* Dashboard Routes (These have their own Layout defined internally) */}
            <Route path="/student/dashboard" element={
              <ProtectedRoute requiredRole={UserRole.STUDENT}>
                <StudentDashboard />
              </ProtectedRoute>
            } />

            <Route path="/agent/dashboard" element={
              <ProtectedRoute requiredRole={UserRole.AGENT}>
                <AgentDashboard />
              </ProtectedRoute>
            } />

            <Route path="/agent/forms" element={
              <ProtectedRoute requiredRole={UserRole.AGENT}>
                <AgentForms />
              </ProtectedRoute>
            } />

            <Route path="/landlord/dashboard" element={
              <ProtectedRoute requiredRole={UserRole.LANDLORD}>
                <LandlordDashboard />
              </ProtectedRoute>
            } />

            <Route path="/admin/dashboard" element={
              <ProtectedRoute requiredRole={UserRole.ADMIN}>
                <AdminDashboard />
              </ProtectedRoute>
            } />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
};

export default App;